<template>
  <div class="caregiver-dashboard">
    <div class="dashboard-header mb-4">
      <h2>My Dashboard</h2>
      <div class="dashboard-actions" v-if="isAdmin">
        <button @click="showPasswordChangeModal = true" class="btn-change-password">
          <i class="fa fa-lock"></i>
          Change Password
        </button>
      </div>
      <div class="dashboard-date">
        {{ currentDate }}
      </div>
    </div>

    <!-- Password Change Modal -->
    <ChangePasswordModal
      v-model="showPasswordChangeModal"
      :isForced="false"
      @success="handlePasswordChangeSuccess"
    />

    <!-- Stats Section -->
    <div class="section-card mb-4">
      <div class="section-header">
        <h3>My Stats</h3>
        <div class="stats-period-selector">
          <button
            v-for="period in statsPeriods"
            :key="period.value"
            class="period-btn"
            :class="{ active: selectedPeriod === period.value }"
            @click="changeStatsPeriod(period.value)"
          >
            {{ period.label }}
          </button>
        </div>
      </div>
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-icon shifts">
            <i class="fa fa-calendar-check"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.shiftsWorked || 0 }}</div>
            <div class="stat-label">Shifts Worked</div>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon hours">
            <i class="fa fa-clock"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.hoursWorked || '0.00' }}</div>
            <div class="stat-label">Hours Worked</div>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon scheduled">
            <i class="fa fa-hourglass-half"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.scheduledHours || '0.00' }}</div>
            <div class="stat-label">Scheduled Hours</div>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon mileage">
            <i class="fa fa-car"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.milesDriven || '0.0' }}</div>
            <div class="stat-label">Mileage</div>
            <div
              v-if="stats.mileageAllowance"
              class="allowance-line"
              :class="{
                'allowance-warn':
                  stats.mileageAllowance.approvedDollars >=
                  (stats.mileageAllowance.capDollars * stats.mileageAllowance.thresholdPercent) / 100,
                'allowance-over': stats.mileageAllowance.remainingDollars < 0,
              }"
            >
              ${{ stats.mileageAllowance.approvedDollars.toFixed(2) }} of
              ${{ stats.mileageAllowance.capDollars.toFixed(2) }} allowance this
              pay period ·
              {{
                stats.mileageAllowance.remainingDollars >= 0
                  ? `$${stats.mileageAllowance.remainingDollars.toFixed(2)} left`
                  : `$${Math.abs(stats.mileageAllowance.remainingDollars).toFixed(2)} over`
              }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Today's Schedule Section -->
    <div class="section-card mb-4">
      <div class="section-header">
        <h3>Today's Schedule</h3>
      </div>
      <div v-if="loading" class="text-center py-4">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
      </div>
      <div v-else-if="todayShifts.length > 0" class="schedule-list">
        <div
          v-for="shift in todayShifts"
          :key="shift.id"
          class="schedule-item"
          @click="goToShift(shift.id)"
        >
          <div class="schedule-time">
            {{ formatTime(shift.scheduled_start) }} - {{ formatTime(shift.scheduled_end) }}
          </div>
          <span class="status-badge badge" :class="getStatusBadgeClass(shift.shift_status)">
            {{ formatStatus(shift.shift_status) }}
          </span>
          <div class="client-name">{{ getClientName(shift) }}</div>
          <div class="shift-codes" v-if="getShiftCodes(shift)">
            {{ getShiftCodes(shift) }}
          </div>
          <div class="schedule-arrow">
            <i class="fa fa-chevron-right"></i>
          </div>
        </div>
      </div>
      <div v-else class="no-data">
        <i class="fa fa-calendar-times fa-2x mb-2"></i>
        <p>No shifts scheduled for today</p>
      </div>
    </div>

    <!-- Pending CST Acknowledgments -->
    <div v-if="pendingCsts.length > 0" class="section-card mb-4">
      <div class="section-header">
        <h3><i class="fa fa-file-signature mr-2"></i> Pending CST Acknowledgments</h3>
      </div>
      <div class="pending-cst-list">
        <div v-for="cst in pendingCsts" :key="cst.id" class="pending-cst-item">
          <div class="pending-cst-info">
            <div class="pending-cst-client">{{ cst.client_first_name }} {{ cst.client_last_name }}</div>
            <div class="pending-cst-version">Version {{ cst.version }}</div>
          </div>
          <el-button size="small" type="primary" @click="openAcknowledgeModal(cst)">
            <i class="fa fa-signature"></i> Acknowledge
          </el-button>
        </div>
      </div>
    </div>

    <!-- My Clients Section -->
    <div class="section-card mb-4">
      <div class="section-header">
        <h3><i class="fa fa-users mr-2"></i> My Clients</h3>
      </div>
      <div v-if="loading" class="text-center py-4">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
      </div>
      <template v-else>
        <el-tabs v-model="activeProgram" type="card" v-if="programNames.length > 0" class="dark-tabs">
          <el-tab-pane
            v-for="prog in programNames"
            :key="prog"
            :label="prog"
            :name="prog"
          >
            <div class="client-grid">
              <div
                v-for="client in getClientsForProgram(prog)"
                :key="client.id"
                class="client-card"
              >
                <div class="client-card__header">
                  <span class="client-card__name">{{ client.first_name }} {{ client.last_name }}</span>
                </div>
                <div class="client-card__details">
                  <p v-if="getClientAddress(client)">
                    <i class="fa fa-map-marker-alt"></i> {{ getClientAddress(client) }}
                  </p>
                  <p v-if="client.secondary_phone">
                    <i class="fa fa-phone"></i> {{ client.secondary_phone }}
                  </p>
                  <div v-if="getEmergencyContact(client)" class="client-card__emergency">
                    <strong>Emergency:</strong> {{ getEmergencyContact(client).name }}
                    <span v-if="getEmergencyContact(client).phone"> - {{ getEmergencyContact(client).phone }}</span>
                  </div>
                </div>
                <div class="client-card__actions">
                  <el-button v-if="client.has_active_cst" size="mini" @click="viewClientCst(client.id)">
                    <i class="fa fa-file-medical"></i> CST
                  </el-button>
                </div>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
        <div v-else class="no-data">
          <i class="fa fa-users fa-2x mb-2"></i>
          <p>No clients assigned</p>
        </div>
      </template>
    </div>

    <!-- Upcoming Shifts Section -->
    <div class="section-card mb-4">
      <div class="section-header">
        <h3><i class="fa fa-calendar-week mr-2"></i> Upcoming Shifts (Next 7 Days)</h3>
      </div>
      <div v-if="loading" class="text-center py-4">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
      </div>
      <template v-else>
        <AIVTable
          :columns="upcomingShiftColumns"
          :data="upcomingShifts"
          :modal-table="true"
          v-if="upcomingShifts.length > 0"
        >
          <template #cell-date="{ row }">
            {{ formatDateShort(row.scheduled_start) }}
          </template>
          <template #cell-time="{ row }">
            {{ formatTime(row.scheduled_start) }} - {{ formatTime(row.scheduled_end) }}
          </template>
          <template #cell-clients="{ row }">
            {{ (row.clients || []).map(c => `${c.first_name} ${c.last_name}`).join(', ') }}
          </template>
          <template #cell-status="{ row }">
            <el-tag size="mini" :type="shiftStatusType(row.shift_status)">{{ row.shift_status || 'Scheduled' }}</el-tag>
          </template>
        </AIVTable>
        <div v-else class="no-data">
          <i class="fa fa-calendar-check fa-2x mb-2"></i>
          <p>No upcoming shifts</p>
        </div>
      </template>
    </div>

    <!-- Time Off Requests Section -->
    <div class="section-card mb-4">
      <div class="section-header">
        <h3>Upcoming Time Off Requests</h3>
        <button @click="goToTimeOffRequests" class="btn btn-link btn-sm">
          View All
        </button>
      </div>
      <div v-if="loading" class="text-center py-4">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
      </div>
      <div v-else-if="upcomingTimeOffRequests.length > 0" class="time-off-list">
        <div
          v-for="request in upcomingTimeOffRequests"
          :key="request.id"
          class="time-off-item"
        >
          <div class="time-off-icon">
            <i class="fa fa-umbrella-beach"></i>
          </div>
          <div class="time-off-details">
            <div class="time-off-dates">
              {{ formatDate(request.start_date) }} - {{ formatDate(request.end_date) }}
            </div>
            <div class="time-off-type">{{ request.reason || 'Time Off' }}</div>
          </div>
          <div class="time-off-status">
            <span class="status-badge" :class="getTimeOffStatusClass(request.status)">
              {{ formatTimeOffStatus(request.status) }}
            </span>
          </div>
        </div>
      </div>
      <div v-else class="no-data">
        <i class="fa fa-check-circle fa-2x mb-2"></i>
        <p>No upcoming time off requests</p>
      </div>
    </div>

    <!-- CST View Modal -->
    <CstViewModal v-model="showCstModal" :cst="selectedCst" />

    <!-- CST Acknowledge Modal -->
    <CstAcknowledgeModal v-model="showAckModal" :cst="ackCst" @acknowledged="onCstAcknowledged" />
  </div>
</template>

<script>
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { timeOffRequestsService } from "@/serviceHandlers/timeOffRequestsService";
import { staffDashboardService } from "@/serviceHandlers/staffDashboardService";
import { useLoginStore } from "@/stores/loginStore";
import ChangePasswordModal from "@/components/ChangePasswordModal.vue";
import CstViewModal from "@/components/CstViewModal.vue";
import CstAcknowledgeModal from "@/components/CstAcknowledgeModal.vue";
import { cstService } from "@/serviceHandlers/cstService";
import dayjs from "dayjs";
import { parseDateOnly } from "@/utils/dateOnly";

export default {
  name: "CaregiverDashboard",
  components: {
    ChangePasswordModal,
    CstViewModal,
    CstAcknowledgeModal,
  },
  data() {
    return {
      loading: false,
      showPasswordChangeModal: false,
      stats: {
        shiftsWorked: 0,
        hoursWorked: '0:00',
        scheduledHours: '0:00'
      },
      todayShifts: [],
      upcomingTimeOffRequests: [],
      selectedPeriod: 'this_week',
      statsPeriods: [
        { label: 'This Week', value: 'this_week' },
        { label: 'This Pay Period', value: 'this_pay_period' }
      ],
      loginStore: useLoginStore(),
      // My Clients data
      clientData: { clients: [], grouped: {} },
      activeProgram: "",
      // Upcoming Shifts data
      upcomingShifts: [],
      upcomingShiftColumns: [
        { key: "date", label: "Date", sortable: false },
        { key: "time", label: "Time", sortable: false },
        { key: "program", label: "Program", sortable: false, format: (val) => val?.display_name || '-' },
        { key: "clients", label: "Clients", sortable: false },
        { key: "status", label: "Status", sortable: false },
      ],
      // CST modal
      showCstModal: false,
      selectedCst: null,
      // CST acknowledge
      pendingCsts: [],
      showAckModal: false,
      ackCst: null,
    };
  },
  computed: {
    currentDate() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    isAdmin() {
      return this.loginStore.isAdmin;
    },
    programNames() {
      return Object.keys(this.clientData.grouped || {});
    },
  },
  async mounted() {
    await this.loadDashboardData();
  },
  methods: {
    async loadDashboardData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadStats(),
          this.loadTodaySchedule(),
          this.loadUpcomingTimeOffRequests(),
          this.loadMyClients(),
          this.loadUpcomingShifts(),
          this.loadPendingCsts(),
        ]);
      } catch (error) {
        this.$notifyError("Error loading dashboard data: " + error);
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      try {
        const stats = await shiftsService.getStats({ period: this.selectedPeriod });
        this.stats = stats || {
          shiftsWorked: 0,
          hoursWorked: '0:00',
          scheduledHours: '0:00'
        };
      } catch (error) {
        this.$notifyError("Error loading stats: " + error);
      }
    },

    async loadTodaySchedule() {
      try {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

        const params = {
          startDate: startOfDay.toISOString(),
          endDate: endOfDay.toISOString(),
          userType: "Caregiver",
          userId: this.loginStore.user.id,
          perPage: 1000,
        };
        const response = await shiftsService.getByRole(params);
        this.todayShifts = response.shifts || [];
      } catch (error) {
        this.$notifyError("Error loading today's schedule: " + error);
      }
    },

    async loadUpcomingTimeOffRequests() {
      try {
        const response = await timeOffRequestsService.getByCaregiver(this.loginStore.user.id);

        let requests = [];
        if (response && response.data) {
          requests = response.data;
        } else if (Array.isArray(response)) {
          requests = response;
        } else {
          requests = [];
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const upcomingRequests = requests.filter(request => {
          // start_date is DATE-only — a UTC parse lands at yesterday evening
          // locally, silently dropping requests that start today.
          const startDate = parseDateOnly(request.start_date);
          return startDate >= today;
        });

        this.upcomingTimeOffRequests = upcomingRequests
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
          .slice(0, 3);
      } catch (error) {
        this.$notifyError("Error loading time off requests: " + error);
      }
    },

    async loadMyClients() {
      try {
        const clientResp = await staffDashboardService.getMyClients();
        this.clientData = clientResp;
        if (this.programNames.length > 0) {
          this.activeProgram = this.programNames[0];
        }
      } catch (error) {
        this.$notifyError("Error loading clients: " + error);
      }
    },

    async loadUpcomingShifts() {
      try {
        const upcomingResp = await staffDashboardService.getMyShiftsUpcoming();
        this.upcomingShifts = upcomingResp;
      } catch (error) {
        this.$notifyError("Error loading upcoming shifts: " + error);
      }
    },

    async loadPendingCsts() {
      try {
        const result = await cstService.getPendingForCaregiver(this.loginStore.user.id);
        this.pendingCsts = Array.isArray(result) ? result : [];
      } catch (error) {
        this.pendingCsts = [];
      }
    },

    openAcknowledgeModal(cst) {
      this.ackCst = cst;
      this.showAckModal = true;
    },

    async onCstAcknowledged() {
      await this.loadPendingCsts();
    },

    async changeStatsPeriod(period) {
      this.selectedPeriod = period;
      await this.loadStats();
    },

    formatTime(datetime) {
      if (!datetime) return "";
      return dayjs(datetime).format("h:mm A");
    },

    formatDate(date) {
      if (!date) return "";
      const [year, month, day] = date.split('T')[0].split('-');
      const localDate = new Date(year, month - 1, day);
      return localDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    formatDateShort(d) {
      return d ? dayjs(d).format("MM/DD/YYYY") : "";
    },

    formatStatus(status) {
      // Recurring shift templates sometimes have a null status — treat them as Scheduled.
      const effective = status || 'Scheduled';
      const statusMap = {
        'Scheduled': 'Scheduled',
        'Clocked In': 'In Progress',
        'Clocked Out': 'Completed',
        'Approved': 'Approved',
      };
      return statusMap[effective] || effective;
    },

    getStatusClass(status) {
      const effective = status || 'Scheduled';
      const classMap = {
        'Scheduled': 'status-scheduled',
        'Clocked In': 'status-in-progress',
        'Clocked Out': 'status-completed',
        'Approved': 'status-approved',
      };
      return classMap[effective] || 'status-default';
    },

    getStatusBadgeClass(status) {
      const effective = status || 'Scheduled';
      const classMap = {
        'Scheduled': 'badge-info',
        'Clocked In': 'badge-success',
        'Clocked Out': 'badge-warning',
        'Approved': 'badge-primary',
      };
      return classMap[effective] || 'badge-secondary';
    },

    getShiftCodes(shift) {
      if (shift.payer === 'Non Service' && shift.non_service_codes && shift.non_service_codes.length > 0) {
        const codes = shift.non_service_codes.map(sc => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(', ') : null;
      }

      if (shift.service_codes && shift.service_codes.length > 0) {
        const codes = shift.service_codes.map(sc => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(', ') : null;
      }

      return null;
    },

    formatTimeOffStatus(status) {
      const statusMap = {
        'pending': 'Pending',
        'approved': 'Approved',
        'denied': 'Denied',
      };
      return statusMap[status] || status;
    },

    getTimeOffStatusClass(status) {
      const classMap = {
        'pending': 'status-pending',
        'approved': 'status-approved',
        'denied': 'status-denied',
      };
      return classMap[status] || 'status-default';
    },

    getClientName(shift) {
      if (shift.clients && shift.clients.length > 0) {
        const client = shift.clients[0];
        const result = client.name ||
                      `${client.first_name || ''} ${client.last_name || ''}`.trim() ||
                      'Client';
        return result;
      }
      return 'Unassigned';
    },

    getClientsForProgram(progName) {
      return this.clientData.grouped?.[progName] || [];
    },

    getClientAddress(client) {
      const addr = (client.addresses || [])[0];
      if (!addr) return null;
      return `${addr.line1 || ""}${addr.line2 ? " " + addr.line2 : ""}, ${addr.city || ""}, ${addr.state || ""} ${addr.zip || ""}`.trim();
    },

    getEmergencyContact(client) {
      return (client.contacts || []).find((c) => c.contact_type === "emergency") || null;
    },

    async viewClientCst(clientId) {
      this.$spinner.show();
      try {
        this.selectedCst = await staffDashboardService.getClientCst(clientId);
        this.showCstModal = true;
      } catch {
        this.$notifyError("No active CST found for this client.");
      } finally {
        this.$spinner.hide();
      }
    },

    shiftStatusType(status) {
      const effective = status || 'Scheduled';
      const map = { Scheduled: "", "Clocked In": "warning", "Clocked Out": "info", Approved: "success", Missed: "danger" };
      return map[effective] || "";
    },

    goToShift(shiftId) {
      this.$router.push(`/pages/shifts?id=${shiftId}`);
    },

    goToTimeOffRequests() {
      this.$router.push('/pages/time-off');
    },

    handlePasswordChangeSuccess() {
      this.showPasswordChangeModal = false;
      this.$notifySuccess("Password changed successfully!");
    },
  },
};
</script>

<style scoped>
.caregiver-dashboard {
  padding: 1rem;
  color: #e0e0e0;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.dashboard-header h2 {
  color: #fff;
  margin: 0;
}

.dashboard-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-change-password {
  background: rgba(23, 162, 184, 0.2);
  color: #17a2b8;
  border: 1px solid rgba(23, 162, 184, 0.4);
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-change-password:hover {
  background: rgba(23, 162, 184, 0.3);
  border-color: rgba(23, 162, 184, 0.6);
  color: #17a2b8;
}

.btn-change-password i {
  font-size: 0.875rem;
}

.dashboard-date {
  color: #b3a6a6;
  font-size: 0.9rem;
}

.section-card {
  background: rgba(37, 55, 65, 0.9);
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.section-header h3 {
  color: #fff;
  margin: 0;
  font-size: 1.25rem;
}

/* Stats Section */
.stats-period-selector {
  display: flex;
  gap: 0.5rem;
}

.period-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #b3a6a6;
  padding: 0.4rem 0.8rem;
  border-radius: 0.25rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.period-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.period-btn.active {
  background: #17a2b8;
  border-color: #17a2b8;
  color: #fff;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.5rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.stat-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.stat-icon.shifts {
  background: rgba(23, 162, 184, 0.2);
  color: #17a2b8;
}

.stat-icon.hours {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.stat-icon.scheduled {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.stat-icon.mileage {
  background: rgba(111, 66, 193, 0.2);
  color: #a98eda;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #fff;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.85rem;
  color: #b3a6a6;
  margin-top: 0.25rem;
}

.allowance-line {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 0.2rem;
}

.allowance-line.allowance-warn {
  color: #e6a23c;
}

.allowance-line.allowance-over {
  color: #f56c6c;
}

/* Today's Schedule Section */
.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.schedule-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.schedule-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.schedule-time {
  color: #fff;
  font-weight: 500;
  min-width: 140px;
  font-size: 0.9rem;
}

.client-name {
  font-size: 0.95rem;
  color: #fff;
  margin-left: 0.75rem;
}

.shift-codes {
  color: #fff;
  font-size: 0.85rem;
  margin-left: 0.75rem;
  flex: 1;
}

.badge {
  font-size: 0.85rem;
  padding: 0.35rem 0.75rem;
  border-radius: 1rem;
  display: inline-block;
  white-space: nowrap;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.status-badge.badge-info {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  );
  color: white;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-primary {
  background-color: #007bff;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.schedule-arrow {
  color: #b3a6a6;
  margin-left: auto;
}

/* My Clients Section */
.client-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.client-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 1rem;
  transition: all 0.2s ease;
}

.client-card:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.client-card__header {
  margin-bottom: 0.5rem;
}

.client-card__name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
}

.client-card__details p {
  color: #b3a6a6;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.client-card__details i {
  width: 16px;
  text-align: center;
  margin-right: 0.25rem;
}

.client-card__emergency {
  color: #e6a23c;
  font-size: 0.85rem;
  margin-top: 0.5rem;
}

.client-card__actions {
  margin-top: 0.75rem;
}

/* Dark tabs styling to match section-card theme */
.dark-tabs :deep(.el-tabs__header) {
  margin-bottom: 1rem;
}

.dark-tabs :deep(.el-tabs__item) {
  color: #b3a6a6;
}

.dark-tabs :deep(.el-tabs__item.is-active) {
  color: #fff;
}

.dark-tabs :deep(.el-tabs__item:hover) {
  color: #fff;
}

/* Time Off Requests Section */
.time-off-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.time-off-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.time-off-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.time-off-details {
  flex: 1;
}

.time-off-dates {
  font-size: 1rem;
  color: #fff;
  margin-bottom: 0.25rem;
}

.time-off-type {
  font-size: 0.85rem;
  color: #b3a6a6;
}

.time-off-status {
  margin-left: auto;
}

.status-badge {
  font-size: 0.85rem;
  padding: 0.35rem 0.75rem;
  border-radius: 1rem;
  display: inline-block;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.status-pending {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-approved {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.status-denied {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

/* Pending CST Acknowledgments */
.pending-cst-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.pending-cst-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: rgba(255, 193, 7, 0.08);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 0.5rem;
}

.pending-cst-client {
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
}

.pending-cst-version {
  font-size: 0.85rem;
  color: #b3a6a6;
  margin-top: 0.25rem;
}

/* No Data */
.no-data {
  text-align: center;
  padding: 3rem 1rem;
  color: #b3a6a6;
}

.no-data i {
  color: rgba(255, 255, 255, 0.3);
}

.no-data p {
  margin: 0;
  font-size: 0.9rem;
}

/* Button styles */
.btn-link {
  color: #17a2b8;
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.btn-link:hover {
  color: #138496;
  background: rgba(23, 162, 184, 0.1);
}

/* Responsive */
@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stats-period-selector {
    flex-wrap: wrap;
  }

  .period-btn {
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
  }

  .schedule-time {
    min-width: 100px;
    font-size: 0.85rem;
  }

  .schedule-item {
    padding: 0.75rem;
    gap: 0.5rem;
  }

  .client-name {
    font-size: 0.9rem;
    margin-left: 0.5rem;
  }

  .shift-codes {
    font-size: 0.8rem;
    margin-left: 0.5rem;
  }

  .badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }

  .client-grid {
    grid-template-columns: 1fr;
  }
}
</style>
